import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import {
  Plus,
  Edit,
  Search,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';
import StatusFilterDropdown from '../../components/filters/StatusFilterDropdown';
import inquiryResponsibleRoleCapacityService from '../../api/inquiryResponsibleRoleCapacityService';
import { getRoles } from '../../api/registrationService';
import { getRoleCodeFromId } from '../../utils/roleMapper';

const PAGE_SIZE = 10;
const ALLOWED_ROLES = new Set(['ADMIN', 'SUPER_ADMIN']);

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const emptyForm = () => ({
  responsibleRoleId: '',
  maxActiveTickets: '',
  description: '',
  status: true
});

/**
 * Inquiry Responsible Role Capacity — max active tickets per Matrix Responsible Role.
 * ADMIN / SUPER_ADMIN only.
 */
const InquiryResponsibleRoleCapacityManagement = () => {
  const roleId = Number(sessionStorage.getItem('roleId') || sessionStorage.getItem('role_id') || 0);
  const roleCode = (getRoleCodeFromId(roleId) || sessionStorage.getItem('roleCode') || '').toUpperCase();
  const canAccess = ALLOWED_ROLES.has(roleCode);

  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    if (!canAccess) return;
    (async () => {
      try {
        const response = await getRoles();
        const list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : Array.isArray(response)
              ? response
              : [];
        setRoles(list.filter((r) => r?.roleId != null));
      } catch (e) {
        console.error('Failed to load roles', e);
        setRoles([]);
      }
    })();
  }, [canAccess]);

  const loadPage = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const statusParam =
        statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined;
      const pageData = await inquiryResponsibleRoleCapacityService.search({
        search: debouncedSearch,
        status: statusParam,
        page: currentPage,
        size: PAGE_SIZE
      });
      const content = Array.isArray(pageData?.content) ? pageData.content : [];
      setRows(content);
      setTotalPages(pageData?.totalPages ?? 0);
      setTotalElements(pageData?.totalElements ?? content.length);
    } catch (error) {
      console.error('Error loading role capacities:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to load capacities');
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [canAccess, debouncedSearch, statusFilter, currentPage]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  if (!canAccess) {
    return <Navigate to="/home" replace />;
  }

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      responsibleRoleId: row.responsibleRoleId != null ? String(row.responsibleRoleId) : '',
      maxActiveTickets: row.maxActiveTickets != null ? String(row.maxActiveTickets) : '',
      description: row.description || '',
      status: row.status !== false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const responsibleRoleId = Number(formData.responsibleRoleId);
    const maxActiveTickets = Number(formData.maxActiveTickets);
    if (!Number.isInteger(responsibleRoleId) || responsibleRoleId <= 0) {
      toast.error('Select a Responsible Role');
      return;
    }
    if (!Number.isInteger(maxActiveTickets) || maxActiveTickets < 1) {
      toast.error('Max active tickets must be a whole number of at least 1');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        responsibleRoleId,
        maxActiveTickets,
        description: formData.description?.trim() || '',
        status: formData.status !== false
      };
      if (editing?.id) {
        await inquiryResponsibleRoleCapacityService.update(editing.id, payload);
        toast.success('Capacity updated successfully');
      } else {
        await inquiryResponsibleRoleCapacityService.create(payload);
        toast.success('Capacity created successfully');
      }
      setShowModal(false);
      setEditing(null);
      await loadPage();
    } catch (error) {
      console.error('Error saving capacity:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save capacity');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (row) => {
    const next = !(row.status !== false);
    try {
      await inquiryResponsibleRoleCapacityService.updateStatus(row.id, next);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
      toast.success(`Capacity ${next ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update status');
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-6 w-6" />
            Inquiry Role Capacity
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Maximum active tickets per Responsible Role for escalation auto-assignment
          </p>
        </div>
        <Button onClick={openCreate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Capacity
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-base">Configurations</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                className="pl-8 w-56"
                placeholder="Search description…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <StatusFilterDropdown
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_FILTER_OPTIONS}
            />
            <Button variant="outline" size="sm" onClick={loadPage} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No capacity configurations found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="py-2 pr-4">Responsible Role</th>
                    <th className="py-2 pr-4">Max Active Tickets</th>
                    <th className="py-2 pr-4">Description</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="py-3 pr-4 font-medium">
                        {row.responsibleRoleName || `Role #${row.responsibleRoleId}`}
                      </td>
                      <td className="py-3 pr-4">{row.maxActiveTickets}</td>
                      <td className="py-3 pr-4 text-gray-600">{row.description || '—'}</td>
                      <td className="py-3 pr-4">
                        <Badge className={row.status !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {row.status !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(row)}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Switch
                          checked={row.status !== false}
                          onCheckedChange={() => handleToggleStatus(row)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              {totalElements} total · page {currentPage + 1} of {Math.max(totalPages, 1)}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 0}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage + 1 >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => !saving && setShowModal(false)}
        title={editing ? 'Edit Role Capacity' : 'Add Role Capacity'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Responsible Role</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={formData.responsibleRoleId}
              onChange={(e) => setFormData((f) => ({ ...f, responsibleRoleId: e.target.value }))}
              disabled={!!editing}
            >
              <option value="">Select role…</option>
              {roles.map((r) => {
                const id = r.roleId ?? r.id;
                const label = r.label || r.roleName || r.name || r.roleCode || `Role ${id}`;
                return (
                  <option key={id} value={id}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Active Tickets</label>
            <Input
              type="number"
              min={1}
              value={formData.maxActiveTickets}
              onChange={(e) => setFormData((f) => ({ ...f, maxActiveTickets: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <Textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Active</span>
            <Switch
              checked={formData.status !== false}
              onCheckedChange={(v) => setFormData((f) => ({ ...f, status: v }))}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InquiryResponsibleRoleCapacityManagement;
